import React, { FC, useEffect, useState } from 'react';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { Image, Spin } from 'antd';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import PdfTemplate from 'shared/assets/images/PdfTemplate.png';
import {
  useGetUserVerificationDocumentsQuery,
  VerificationDocument,
  VerificationDocumentType,
  useUploadVerificationDocumentMutation,
  UserAccountType,
  useRole,
  useGetUserByIdQuery,
  VerificationFileStatus,
  PersonalVerificationDocumentType,
  BusinessVerificationDocumentType,
} from 'entities/User';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as CheckIcon } from 'shared/assets/icons/CheckMark.svg';
import { ReactComponent as RejectIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/newIcons/ClockIcon.svg';
import { ManageVerificationButtons } from './ManageVerificationButtons';
import { MimeTypes } from 'features/UploadModal';
import { openPdf } from 'shared/utils/helpers/openPdf';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accountType: UserAccountType;
  userId: string;
}

interface LocalFilePreview {
  file: File;
  preview: string;
}

export const UploadDocumentsModal: FC<Props> = ({ isOpen, onClose, accountType, userId }) => {
  const { data: documents, isFetching, refetch } = useGetUserVerificationDocumentsQuery({ userId }, { skip: !isOpen });

  const { refetch: refetchUser } = useGetUserByIdQuery({ userId });

  const { t } = useAppTranslation(['profile', 'common']);
  const { isManagement, isCustomer } = useRole();
  const { isDesktop, isMobile } = useScreenBreakpoints();

  const [uploadRequest, { isLoading: isUploading }] = useUploadVerificationDocumentMutation();
  const [localFiles, setLocalFiles] = useState<Record<string, LocalFilePreview | null>>({});
  const [previewVisibleMap, setPreviewVisibleMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setLocalFiles({});
      setPreviewVisibleMap({});
    }
  }, [isOpen]);

  const documentFields =
    accountType === UserAccountType.BUSINESS
      ? [
          {
            key: BusinessVerificationDocumentType.COMPANY_REG_CERT,
            label: t('Company registration certificate'),
          },
        ]
      : [
          {
            key: PersonalVerificationDocumentType.PASSPORT_MAIN,
            label: t('Photo of the main passport page'),
          },
          {
            key: PersonalVerificationDocumentType.SELFIE_WITH_PASSPORT,
            label: t('Selfie with main passport page'),
          },
          {
            key: PersonalVerificationDocumentType.PASSPORT_REG,
            label: t('Registration passport photo'),
          },
        ];

  const handleFileSelect = async (documentType: string, fileList: FileList | null): Promise<void> => {
    if (!fileList?.[0]) {
      return;
    }

    const file = fileList[0];

    const reader = new FileReader();

    reader.onloadend = () => {
      setLocalFiles((prev) => ({
        ...prev,
        [documentType]: { file, preview: reader.result as string },
      }));
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = async (): Promise<void> => {
    const orderedFiles: File[] = [];
    const orderedTypes: VerificationDocumentType[] = [];

    documentFields.forEach(({ key }) => {
      const local = localFiles[key];

      if (local) {
        orderedFiles.push(local.file);
        orderedTypes.push(key);
      }
    });

    if (!orderedFiles.length) {
      onClose();

      return;
    }

    try {
      await uploadRequest({
        userId,
        files: orderedFiles,
        documentTypes: orderedTypes,
      }).unwrap();

      await Promise.all([refetch(), refetchUser()]);

      onClose();
    } catch (err) {
      console.error('Ошибка при отправке verification-request', err);
    }
  };

  const getDocument = (type: string): VerificationDocument | undefined => documents?.find((d) => d.documentType === type);

  const renderPreview = (type: string, doc?: VerificationDocument): React.ReactElement => {
    const local = localFiles[type];
    const isRejected = doc?.fileStatus === VerificationFileStatus.REJECTED;
    const isApproved = doc?.fileStatus === VerificationFileStatus.APPROVED;
    const isUploaded = !!doc?.base64;
    const previewSrc = local?.preview ? local.preview : isUploaded ? doc.base64 : null;
    const mimeType = local?.file?.type || doc?.base64?.match(/^data:(.*?);base64,/)?.[1] || '';
    const isPdf = mimeType === MimeTypes.PDF;

    if (previewSrc) {
      const isVisible = previewVisibleMap[type] || false;

      const canUploadNewDoc = isManagement || (!isApproved && (!isCustomer || isRejected));

      return (
        <>
          {isPdf ? (
            <div
              onClick={() => {
                openPdf(doc?.base64 || local?.preview);
              }}
              className="w-full border-0 bg-transparent p-0 cursor-pointer"
            >
              <div className="w-full h-[180px] desktop:h-[200px] rounded-lg flex flex-col items-center">
                <img src={PdfTemplate} alt={t('Open PDF')} className="my-5 w-[100px] h-[100px] object-cover" />

                <div className="flex items-center justify-center bg-black/10">
                  <span className="px-4 py-2 rounded-md bg-white/90 text-green-600 font-medium shadow">{t('Open PDF')}</span>
                </div>
              </div>
            </div>
          ) : (
            <Image
              height={isMobile ? 180 : 200}
              width="100%"
              className="rounded-lg object-cover"
              src={previewSrc}
              preview={{
                visible: isVisible,
                scaleStep: 3,
                src: previewSrc,
                onVisibleChange: (value) => {
                  setPreviewVisibleMap((prev) => ({ ...prev, [type]: value }));
                },
              }}
            />
          )}
          {isRejected && (
            <div>
              <div className="text-sm text-red-500 mt-2">
                <p>{t('Document is rejected')}</p>
                {doc?.note && <p className="text-xs text-gray-500 italic mt-1">{t('Reason')}: </p>}
              </div>
              <div className="mt-2 text-xs text-gray-500 italic bg-red-50 border border-red-100 rounded-md p-2">{doc?.note || ''}</div>
            </div>
          )}

          {canUploadNewDoc && (
            <label className="cursor-pointer text-green-500 mt-3 underline text-sm">
              {local ? t('Replace file') : t('Upload new file')}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                className="hidden"
                onChange={async (e) => {
                  await handleFileSelect(type, e.target.files);
                }}
              />
            </label>
          )}
        </>
      );
    }

    return (
      <label className="flex flex-col items-center justify-center cursor-pointer w-full h-full">
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
          className="hidden"
          onChange={async (e) => {
            await handleFileSelect(type, e.target.files);
          }}
        />
        <div className="text-gray-400 text-sm mt-2">{t('Click or drag file', { ns: 'common' })}</div>
      </label>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={1000}>
      {isFetching ? (
        <div className="flex justify-center items-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="text-center font-semibold text-lg text-primary">{t('Upload documents form')}</div>

          {accountType === UserAccountType.BUSINESS ? (
            <>
              <div className="text-sm text-gray-700 leading-relaxed">
                {t('Please make sure that the uploaded document meets the following requirements')}:
              </div>
              <ul className="list-disc ml-6 text-sm text-gray-600 space-y-1">
                <li>{t('The document must be a high-quality scan or photo')}</li>
                <li>{t('All text must be clearly readable')}</li>
                <li>{t('File must contain the full page of the document')}</li>
              </ul>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-700 leading-relaxed">
                {t('Make sure selfie with passport is compatible with the following')}:
              </div>
              <ol className="list-decimal ml-6 text-sm text-gray-600 space-y-1">
                <li>{t('Photo should have good quality')}</li>
                <li>{t('Passport should be fully visible')}</li>
                <li>{t('Everything should be readable')}</li>
                <li>{t('Face and passport should be visible')}</li>
              </ol>
            </>
          )}

          <p className="text-sm text-gray-500 mt-2">{t('Allowed docs formats')}</p>

          <div className={`grid gap-4 mt-4 ${isDesktop ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {documentFields.map(({ key, label }) => {
              const doc = getDocument(key);
              const status = doc?.fileStatus;

              let borderClass = 'border-gray-300 text-gray-700';
              let badgeColor = 'bg-gray-200 text-gray-700';
              let badgeIcon = <ClockIcon className="mr-2 w-[16px] h-[16px] [&>path]:stroke-white" />;
              let badgeText = t('Verification');

              if (status === VerificationFileStatus.APPROVED) {
                borderClass = 'border-green-500 text-green-600';
                badgeColor = 'bg-green-500 text-white';
                badgeIcon = <CheckIcon className="mr-2 w-[16px] h-[16px] [&>path]:fill-white" />;
                badgeText = t('Approved');
              } else if (status === VerificationFileStatus.REJECTED) {
                borderClass = 'border-red-400 text-red-500';
                badgeColor = 'bg-red-500 text-white';
                badgeIcon = <RejectIcon className="mr-2 w-[16px] h-[16px] [&>path]:stroke-errorLight" />;
                badgeText = t('Rejected');
              }

              return (
                <div
                  key={key}
                  className={`relative flex flex-col border-2 ${
                    status === 'approved' ? 'border-solid' : 'border-dashed'
                  } ${borderClass} rounded-2xl p-4 text-center bg-white transition-all duration-200 hover:shadow-md`}
                >
                  {status && (
                    <div
                      className={`absolute -top-3 -right-3 flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full shadow-sm ${badgeColor}`}
                      style={{ zIndex: 5 }}
                    >
                      {badgeIcon}
                      <span>{badgeText}</span>
                    </div>
                  )}

                  <p className="font-medium mb-3 text-sm md:text-base">{label}</p>

                  {renderPreview(key, doc)}

                  {isManagement && doc?.verificationPhotoId && (
                    <div className="mt-4 flex flex-col gap-2 w-full">
                      <ManageVerificationButtons
                        verificationPhotoId={doc.verificationPhotoId}
                        onSuccess={async () => {
                          await refetch();
                        }}
                        fileStatus={doc.fileStatus}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="pt-2">
            <Button
              size={isMobile ? 'medium' : 'large'}
              className="w-full mt-6 text-base md:text-lg"
              onClick={handleSubmit}
              isLoading={isUploading}
            >
              {t('Done', { ns: 'common' })}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
